/**
 * Estado do Assistente Comercial, compartilhado por toda a aplicação.
 *
 * POR QUE UM ESTADO SÓ
 *
 * O assistente aparece em dois lugares: em destaque no painel inicial e num
 * acionador persistente nas demais telas. São dois pontos de ENTRADA, não duas
 * conversas — o requisito é explícito nisso, e ele tem razão prática: digitar
 * "quero cadastrar uma empresa" no painel, navegar para Empresas e encontrar o
 * assistente sem memória do que acabou de ser dito seria pior do que não ter o
 * atalho. A pessoa perderia o turno e não entenderia por quê.
 *
 * Então a conversa vive aqui, acima da árvore de rotas. Os dois componentes de
 * tela leem e escrevem o MESMO estado. Trocar de página não desmonta nada.
 *
 * O QUE NÃO ESTÁ AQUI
 *
 * Nenhuma regra de negócio, nenhuma decisão sobre o que pode ser executado, e
 * nenhuma interpretação de linguagem. Tudo isso é do backend, de propósito: o que
 * o navegador decide, o navegador pode mentir. Este arquivo manda texto e mostra
 * o que voltou.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from './api';
import { t } from './i18n';

export interface PreviaCampo {
  rotulo: string;
  de: string | null;
  para: string | null;
}

export interface Previa {
  titulo: string;
  alvo: { tipo: string; id: string | null; rotulo: string } | null;
  campos: PreviaCampo[];
  avisos: string[];
}

export interface Opcao {
  valor: string;
  rotulo: string;
  detalhe?: string;
}

/**
 * Plano cognitivo apresentado para confirmação.
 *
 * O `hash` e a `versao` voltam ao servidor junto do "confirmar": é assim que a
 * confirmação vale para o plano que ESTA tela mostrou, e não para outro que
 * tenha sido escrito por cima — duas abas abertas deixam de ser um risco.
 */
export interface PlanoApresentado {
  planId: string;
  versao: number;
  hash: string;
  expiraEmIso: string;
  objetivo: string;
  interpretacao: string;
  /** `rotulo` é o que se mostra; `tipo` fica para diagnóstico (§20.3). */
  passos: { id: string; tipo: string; rotulo: string; escrita: boolean }[];
}

export interface PassoExecutado {
  id: string;
  tipo: string;
  /** O que mostrar quando o passo não tem resumo próprio (§20.3). */
  rotulo?: string;
  estado: string;
  resumo: string;
  motivo: string | null;
  registro: { tipo: string; id: string } | null;
  /** Pedido da etapa de escrita: é por ele que se consulta o desfecho real. */
  requestId?: string | null;
  /**
   * A CATEGORIA da falha, quando houve uma.
   *
   * A tela usa `retentavel` para decidir se oferece repetir, e `oQueFazer` para
   * dizer qual é o próximo passo. Antes disso, toda falha exibia a mesma frase e
   * o mesmo botão — inclusive quando repetir não tinha a menor chance, como em
   * "falta dizer a empresa".
   */
  falha: { causa: string; oQueFazer: string | null; retentavel: boolean } | null;
}

/**
 * Indisponibilidade da camada cognitiva, com o que fazer a respeito.
 *
 * A versão anterior devolvia só uma frase educada: o pedido sumia do campo, não
 * havia o que clicar, e parecia que o CRM inteiro tinha caído junto. Honestidade
 * sem saída é só uma recusa elegante.
 */
export interface Indisponibilidade {
  categoria: string;
  transitoria: boolean;
  incidente: string;
  textoPreservado: string;
  acoesGuiadas: { chave: string; rotulo: string; descricao: string }[];
  camposAproveitados: Record<string, string>;
}

/** De onde a resposta veio (§20.3). */
export interface FonteDaResposta {
  tipo: 'registro' | 'consulta' | 'externo' | 'memoria';
  rotulo: string;
  entidade: { tipo: string; id: string } | null;
  verificado: boolean;
}

export interface RespostaDoAgente {
  conversationId: string;
  mensagem: string;
  previa: Previa | null;
  requestId: string | null;
  termoExigido: string | null;
  registro: { tipo: string; id: string; rotulo: string } | null;
  dados: unknown;
  fontes?: FonteDaResposta[];
  fontesOcultas?: number;
  sugestoes: string[];
  opcoes: Opcao[];
  plano: PlanoApresentado | null;
  passos: PassoExecutado[] | null;
  indisponibilidade: Indisponibilidade | null;
  /** O desfecho como o backend o GARANTE (CERES-IA-002). */
  desfecho?: Desfecho | null;
  estado: 'LIVRE' | 'COLETANDO' | 'AGUARDANDO_CONFIRMACAO' | 'CONCLUIDO';
}

/**
 * O desfecho de uma execução, como o backend o garante (CERES-IA-002).
 *
 * "Nada foi feito" só pode ser dito com `garantia: NADA_APLICADO`. Código HTTP
 * não é garantia: um 502 pode ter chegado depois de o efeito acontecer.
 */
export interface Desfecho {
  status: string;
  garantia: 'APLICADO' | 'NADA_APLICADO' | 'DESCONHECIDO';
  requestId: string | null;
}

export interface Turno {
  id: string;
  papel: 'USER' | 'ASSISTANT';
  texto: string;
  previa?: Previa | null;
  registro?: { tipo: string; id: string; rotulo: string } | null;
  opcoes?: Opcao[];
  plano?: PlanoApresentado | null;
  passos?: PassoExecutado[] | null;
  indisponibilidade?: Indisponibilidade | null;
  /** Turno produzido no modo guiado: a tela marca isso visualmente. */
  guiado?: boolean;
  fontes?: FonteDaResposta[];
  fontesOcultas?: number;
  /** Transcrição bruta, quando veio de voz e foi editada. */
  transcricao?: string | null;
  erro?: boolean;
  /**
   * Falha em que NÃO se sabe se algo foi gravado (tempo esgotado, conexão
   * perdida, servidor que não respondeu). Não é erro vermelho: dizer "não
   * consegui" de uma ação que pode ter acontecido leva a pessoa a repeti-la (P12).
   */
  desconhecido?: boolean;
  /** Desfecho garantido pelo backend, quando a resposta trouxe um. */
  desfecho?: Desfecho | null;
}

/**
 * O que dizer quando o pedido ao assistente falha NA REDE ou no servidor.
 *
 * Três histórias diferentes, que antes viravam o mesmo "Não consegui completar":
 *   - o servidor demorou, a conexão caiu ou a resposta não veio (504, 408, 502,
 *     503): não se sabe se gravou — consulte antes de repetir. Um 502/503 NÃO
 *     prova que nada foi executado: a requisição pode ter chegado à aplicação
 *     antes de o proxy desistir (CERES-IA-002). "Nada foi feito" só com a
 *     garantia do backend, que vem no desfecho;
 *   - a mesma requisição ainda está em processamento (409 da idempotência):
 *     aguarde e consulte;
 *   - o servidor recusou com motivo: a frase dele, em português.
 */
export function classificarFalhaDoAssistente(erro: unknown): { texto: string; desconhecido: boolean } {
  if (erro instanceof ApiError) {
    if (erro.status === 504 || erro.status === 408 || erro.code === 'GATEWAY_TIMEOUT') {
      return { texto: t('assistente.falhaDesconhecida'), desconhecido: true };
    }
    if (erro.status === 502 || erro.status === 503 || erro.code === 'GATEWAY_UNAVAILABLE') {
      return { texto: t('assistente.falhaSemResposta'), desconhecido: true };
    }
    // Só a recusa da IDEMPOTÊNCIA: a recusa de domínio também é 409 `CONFLICT`,
    // e ela tem motivo próprio para mostrar.
    if (
      erro.status === 409 &&
      (erro.details as { motivo?: unknown } | undefined)?.motivo === 'REQUISICAO_EM_PROCESSAMENTO'
    ) {
      return { texto: t('assistente.emProcessamento'), desconhecido: true };
    }
    return { texto: `${erro.mensagemHumana} ${t('assistente.falhaContinuePelasTelas')}`, desconhecido: false };
  }
  // `fetch` rejeita com TypeError quando a conexão cai: o pedido pode ter chegado.
  if (erro instanceof TypeError) {
    return { texto: t('assistente.falhaDesconhecida'), desconhecido: true };
  }
  return { texto: t('assistente.falhaGenerica'), desconhecido: false };
}

/**
 * O que ficou pendente na conversa que se acabou de reabrir (§20.3).
 *
 * Sem isto, retomar mostrava as mensagens e assumia "livre" — enquanto o
 * servidor continuava com o rascunho. A frase seguinte era lida como resposta a
 * uma pergunta que a tela não repetiu.
 */
export interface Retomada {
  estado: 'LIVRE' | 'COLETANDO' | 'AGUARDANDO_CONFIRMACAO' | 'EXPIRADO';
  pendencia: string | null;
  pergunta: string | null;
  plano: PlanoApresentado | null;
}

interface EstadoDoAssistente {
  turnos: Turno[];
  enviando: boolean;
  conversationId: string | null;
  sugestoes: string[];
  opcoes: Opcao[];
  termoExigido: string | null;
  estado: RespostaDoAgente['estado'];
  /** Plano aguardando confirmação, quando há um. */
  plano: PlanoApresentado | null;
  /**
   * Etapa da vez, para a tela dizer o que está acontecendo.
   *
   * Uma espera de vinte segundos sem sinal nenhum parece travamento. Dizer
   * "Consultando o CRM" não acelera nada, mas transforma silêncio em progresso.
   */
  etapa: 'ocioso' | 'entendendo' | 'executando';
  /** Painel lateral aberto? Só afeta as telas que não são o painel inicial. */
  painelAberto: boolean;
  abrirPainel: (textoInicial?: string) => void;
  fecharPainel: () => void;
  enviar: (texto: string, transcricao?: string | null) => Promise<void>;
  /** Segue no MODO GUIADO com a ação escolhida, sem interpretar a frase. */
  seguirGuiado: (chave: string, texto: string) => Promise<void>;
  /**
   * Texto a devolver ao campo depois de uma falha.
   *
   * Fazer a pessoa redigitar um pedido de três linhas porque o servidor teve um
   * problema é cobrar dela o erro do sistema.
   */
  textoARestaurar: string | null;
  limparTextoARestaurar: () => void;
  novaConversa: () => void;
  carregarConversa: (id: string) => Promise<void>;
  /** Pendência da conversa reaberta. `null` quando não há nada em curso. */
  retomada: Retomada | null;
  /**
   * Consulta o desfecho real de uma execução cuja resposta não chegou ou ficou
   * em conferência (CERES-IA-002): pelo pedido, quando se sabe qual é; senão,
   * relendo a conversa no servidor.
   */
  consultarOperacao: (requestId: string | null) => Promise<void>;
  /**
   * Quem está na sessão e em qual organização. Quem informa é
   * `SincronizarAssistente`, montado dentro dos dois provedores — este estado
   * não depende de existir sessão (telas isoladas e testes não têm).
   */
  sincronizarIdentidade: (identidade: string | null) => void;
}

const Ctx = createContext<EstadoDoAssistente | null>(null);

/**
 * Chave de idempotência de UM envio (CERES-IA-002). Repetida só quando o
 * transporte falha; um envio novo da pessoa é um pedido novo, com chave nova.
 */
function novaChave(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Envia a mensagem; se a CONEXÃO cair, repete UMA vez com a mesma chave. O
 * servidor reconhece a repetição: devolve a resposta que já tinha gravado, ou
 * diz que a requisição original ainda está em processamento — nunca processa
 * duas vezes.
 */
async function postarMensagem(corpo: Record<string, unknown>): Promise<RespostaDoAgente> {
  const chave = novaChave();
  try {
    return await api.post<RespostaDoAgente>('/agent/messages', corpo, chave);
  } catch (erro) {
    if (!(erro instanceof TypeError)) throw erro;
    return api.post<RespostaDoAgente>('/agent/messages', corpo, chave);
  }
}

const SUGESTOES_PADRAO = [
  'Quero cadastrar uma nova empresa',
  'Mostre o que precisa da minha atenção hoje',
  'Crie um follow-up para sexta-feira às 9 horas',
  'Registre a reunião que tive hoje',
];

let contador = 0;
const proximoId = (): string => `t${(contador += 1)}`;

export function AssistenteProvider({ children }: { children: ReactNode }): JSX.Element {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sugestoes, setSugestoes] = useState<string[]>(SUGESTOES_PADRAO);
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  const [termoExigido, setTermoExigido] = useState<string | null>(null);
  const [estado, setEstado] = useState<RespostaDoAgente['estado']>('LIVRE');
  const [plano, setPlano] = useState<PlanoApresentado | null>(null);
  const [etapa, setEtapa] = useState<'ocioso' | 'entendendo' | 'executando'>('ocioso');
  const [textoARestaurar, setTextoARestaurar] = useState<string | null>(null);
  const [painelAberto, setPainelAberto] = useState(false);
  const [retomada, setRetomada] = useState<Retomada | null>(null);
  /** `undefined` até a primeira sincronização: antes dela não há troca a detectar. */
  const [identidade, setIdentidade] = useState<string | null | undefined>(undefined);

  /**
   * Guarda o texto que o acionador global recebeu antes de abrir.
   *
   * Existe para o caso "clicou numa sugestão do estado vazio de outra tela": o
   * painel precisa abrir E mandar a frase, e sem isto o clique abriria um painel
   * em branco esperando a pessoa digitar de novo o que ela já pediu.
   */
  const pendente = useRef<string | null>(null);

  /**
   * GERAÇÃO DA CONVERSA (U3).
   *
   * Muda a cada "nova conversa", troca de conversa ou de pessoa/organização. Uma
   * resposta que chega depois da troca pertence a outra conversa e é descartada
   * — antes, ela gravava o `conversationId` e o plano antigos por cima da nova.
   */
  const geracao = useRef(0);

  const enviar = useCallback(
    async (texto: string, transcricao: string | null = null): Promise<void> => {
      const limpo = texto.trim();
      if (limpo === '' || enviando) return;

      // Confirmar um plano é executar; qualquer outra coisa é interpretar. A
      // distinção só serve para a etapa mostrada na tela ser honesta.
      const confirmando = plano !== null && estado === 'AGUARDANDO_CONFIRMACAO';
      setEtapa(confirmando ? 'executando' : 'entendendo');

      setTurnos((atual) => [
        ...atual,
        { id: proximoId(), papel: 'USER', texto: limpo, transcricao },
      ]);
      setEnviando(true);
      setOpcoes([]);
      // A pendência descrevia o estado ANTERIOR ao turno. Mantê-la depois de a
      // pessoa responder mostraria um aviso que já não vale.
      setRetomada(null);
      const minha = geracao.current;

      try {
        const resposta = await postarMensagem({
          texto: limpo,
          ...(conversationId === null ? {} : { conversationId }),
          ...(confirmando
            ? { planoEsperado: { planId: plano.planId, versao: plano.versao, hash: plano.hash } }
            : {}),
          ...(transcricao === null ? {} : { channel: 'VOICE_DICTATION', transcricaoOriginal: transcricao }),
        });
        if (minha !== geracao.current) return;

        setConversationId(resposta.conversationId);
        setEstado(resposta.estado);
        setTermoExigido(resposta.termoExigido);
        setOpcoes(resposta.opcoes ?? []);
        setPlano(resposta.plano ?? null);
        // Falhou: o texto volta para o campo, para tentar de novo sem redigitar.
        setTextoARestaurar(resposta.indisponibilidade?.textoPreservado ?? null);
        if ((resposta.sugestoes ?? []).length > 0) setSugestoes(resposta.sugestoes);

        setTurnos((atual) => [
          ...atual,
          {
            id: proximoId(),
            papel: 'ASSISTANT',
            texto: resposta.mensagem,
            previa: resposta.previa,
            registro: resposta.registro,
            opcoes: resposta.opcoes,
            plano: resposta.plano ?? null,
            passos: resposta.passos ?? null,
            indisponibilidade: resposta.indisponibilidade ?? null,
            fontes: resposta.fontes ?? [],
            fontesOcultas: resposta.fontesOcultas ?? 0,
            desfecho: resposta.desfecho ?? null,
            desconhecido: resposta.desfecho?.garantia === 'DESCONHECIDO',
          },
        ]);
      } catch (erro) {
        if (minha !== geracao.current) return;
        // A falha vira TURNO, e não um toast que some: a pessoa precisa ver onde
        // a conversa parou para saber o que repetir. E o CRM continua utilizável
        // pelas telas — o assistente cair não pode parecer que o sistema caiu.
        const falha = classificarFalhaDoAssistente(erro);
        setTurnos((atual) => [
          ...atual,
          {
            id: proximoId(),
            papel: 'ASSISTANT',
            texto: falha.texto,
            erro: !falha.desconhecido,
            desconhecido: falha.desconhecido,
          },
        ]);
        // O texto volta ao campo — exceto uma CONFIRMAÇÃO de resultado
        // desconhecido: devolver "confirmar" ao campo convidaria a repetir.
        if (!(confirmando && falha.desconhecido)) setTextoARestaurar(limpo);
      } finally {
        setEnviando(false);
        setEtapa('ocioso');
      }
    },
    [conversationId, enviando, plano, estado],
  );

  /**
   * MODO GUIADO.
   *
   * Manda a ação escolhida junto do texto original. O servidor NÃO interpreta a
   * frase: ele abre a ferramenta correspondente, aproveita só o que é inequívoco
   * (e-mail, telefone, CNPJ, data em dígitos) e pergunta o resto.
   */
  const seguirGuiado = useCallback(
    async (chave: string, texto: string): Promise<void> => {
      if (enviando) return;
      setEnviando(true);
      setEtapa('entendendo');
      const minha = geracao.current;
      try {
        const resposta = await postarMensagem({
          texto,
          acaoGuiada: chave,
          ...(conversationId === null ? {} : { conversationId }),
        });
        if (minha !== geracao.current) return;
        setConversationId(resposta.conversationId);
        setEstado(resposta.estado);
        setTermoExigido(resposta.termoExigido ?? null);
        setOpcoes(resposta.opcoes ?? []);
        // O plano anterior não vale mais: o modo guiado é outro pedido.
        setPlano(resposta.plano ?? null);
        setTextoARestaurar(null);
        setTurnos((atual) => [
          ...atual,
          {
            id: proximoId(),
            papel: 'ASSISTANT',
            texto: resposta.mensagem,
            guiado: true,
            previa: resposta.previa,
            opcoes: resposta.opcoes,
            passos: resposta.passos ?? null,
            desfecho: resposta.desfecho ?? null,
            desconhecido: resposta.desfecho?.garantia === 'DESCONHECIDO',
          },
        ]);
      } catch (erro) {
        if (minha !== geracao.current) return;
        // Antes, uma falha aqui virava rejeição sem tratamento: nenhum turno.
        const falha = classificarFalhaDoAssistente(erro);
        setTurnos((atual) => [
          ...atual,
          { id: proximoId(), papel: 'ASSISTANT', texto: falha.texto, erro: !falha.desconhecido, desconhecido: falha.desconhecido },
        ]);
        setTextoARestaurar(texto);
      } finally {
        setEnviando(false);
        setEtapa('ocioso');
      }
    },
    [conversationId, enviando],
  );

  const abrirPainel = useCallback(
    (textoInicial?: string): void => {
      setPainelAberto(true);
      if (textoInicial !== undefined && textoInicial.trim() !== '') {
        pendente.current = textoInicial;
        void enviar(textoInicial);
        pendente.current = null;
      }
    },
    [enviar],
  );

  const novaConversa = useCallback((): void => {
    geracao.current += 1;
    setTurnos([]);
    setConversationId(null);
    setSugestoes(SUGESTOES_PADRAO);
    setOpcoes([]);
    setTermoExigido(null);
    setEstado('LIVRE');
    setPlano(null);
    setRetomada(null);
    setTextoARestaurar(null);
  }, []);

  /**
   * ISOLAMENTO POR PESSOA E ORGANIZAÇÃO (U2).
   *
   * O estado mora acima do roteador e sobrevive à navegação — e sobrevivia
   * também ao logout e à troca de organização: a próxima mensagem saía com o
   * `conversationId` (e o plano) da organização ou da pessoa anterior. Mudou
   * quem está na sessão ou em qual organização, a conversa recomeça vazia.
   */
  const identidadeAnterior = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (identidade === undefined) return;
    if (identidadeAnterior.current === undefined) {
      identidadeAnterior.current = identidade;
      return;
    }
    if (identidadeAnterior.current === identidade) return;
    identidadeAnterior.current = identidade;
    novaConversa();
    setPainelAberto(false);
  }, [identidade, novaConversa]);

  /**
   * Reabrir uma conversa — NO ESTADO EM QUE ELA PAROU.
   *
   * O servidor diz o que ficou pendente; esta função não deduz nada do
   * histórico. Deduzir daria certo hoje e erraria no dia em que o servidor
   * mudasse de ideia sobre o que conta como pendência — e o erro apareceria como
   * um botão de confirmar que não confirma nada.
   */
  const carregarConversa = useCallback(async (id: string): Promise<void> => {
    geracao.current += 1;
    const minha = geracao.current;
    let dados: {
      id: string;
      mensagens: { papel: string; conteudo: string }[];
      estado?: Retomada['estado'];
      pendencia?: string | null;
      pergunta?: string | null;
      plano?: PlanoApresentado | null;
    };
    try {
      dados = await api.get(`/agent/conversations/${id}`);
    } catch (erro) {
      if (minha !== geracao.current) return;
      // Antes, a falha ao reabrir ficava sem tratamento e a tela, sem resposta.
      setTurnos([
        { id: proximoId(), papel: 'ASSISTANT', texto: classificarFalhaDoAssistente(erro).texto, erro: true },
      ]);
      return;
    }
    if (minha !== geracao.current) return;
    setTermoExigido(null);
    setTextoARestaurar(null);
    setConversationId(dados.id);
    setTurnos(
      dados.mensagens
        .filter((m) => m.papel === 'USER' || m.papel === 'ASSISTANT')
        .map((m) => ({
          id: proximoId(),
          papel: m.papel as 'USER' | 'ASSISTANT',
          texto: m.conteudo,
        })),
    );
    setOpcoes([]);

    const situacao = dados.estado ?? 'LIVRE';
    // EXPIRADO não é estado de conversa: é o aviso de que o plano venceu. Para o
    // que a pessoa pode fazer agora, a conversa está livre.
    setEstado(situacao === 'EXPIRADO' ? 'LIVRE' : situacao);
    setPlano(dados.plano ?? null);
    setRetomada(
      situacao === 'LIVRE' && (dados.pendencia ?? null) === null
        ? null
        : {
            estado: situacao,
            pendencia: dados.pendencia ?? null,
            pergunta: dados.pergunta ?? null,
            plano: dados.plano ?? null,
          },
    );
  }, []);

  // Estável: o painel lateral depende dela num efeito, e uma função nova a cada
  // turno refazia o efeito — o foco pulava para o campo no meio da leitura.
  const fecharPainel = useCallback((): void => setPainelAberto(false), []);

  const consultarOperacao = useCallback(
    async (requestId: string | null): Promise<void> => {
      if (requestId === null) {
        // Sem o pedido (a resposta inteira se perdeu), o servidor ainda sabe em
        // que ponto a conversa está: relê-la é a consulta possível.
        if (conversationId !== null) await carregarConversa(conversationId);
        return;
      }
      const minha = geracao.current;
      try {
        const r = await api.get<{
          status: string;
          mensagem: string;
          garantia?: Desfecho['garantia'];
          entityType?: string;
          entityId?: string | null;
        }>(`/agent/requests/${requestId}`);
        if (minha !== geracao.current) return;
        const garantia = r.garantia ?? 'DESCONHECIDO';
        setTurnos((atual) => [
          ...atual,
          {
            id: proximoId(),
            papel: 'ASSISTANT',
            texto: r.mensagem,
            desfecho: { status: r.status, garantia, requestId },
            desconhecido: garantia === 'DESCONHECIDO',
            registro:
              r.entityId != null && r.entityType !== undefined
                ? { tipo: r.entityType, id: r.entityId, rotulo: r.mensagem }
                : null,
          },
        ]);
      } catch (erro) {
        if (minha !== geracao.current) return;
        const falha = classificarFalhaDoAssistente(erro);
        setTurnos((atual) => [
          ...atual,
          { id: proximoId(), papel: 'ASSISTANT', texto: falha.texto, erro: !falha.desconhecido, desconhecido: falha.desconhecido },
        ]);
      }
    },
    [conversationId, carregarConversa],
  );

  const valor = useMemo<EstadoDoAssistente>(
    () => ({
      turnos,
      enviando,
      conversationId,
      sugestoes,
      opcoes,
      termoExigido,
      estado,
      plano,
      etapa,
      painelAberto,
      abrirPainel,
      fecharPainel,
      enviar,
      seguirGuiado,
      textoARestaurar,
      limparTextoARestaurar: () => setTextoARestaurar(null),
      novaConversa,
      carregarConversa,
      retomada,
      consultarOperacao,
      sincronizarIdentidade: setIdentidade,
    }),
    [
      turnos,
      enviando,
      conversationId,
      sugestoes,
      opcoes,
      termoExigido,
      estado,
      plano,
      etapa,
      painelAberto,
      abrirPainel,
      fecharPainel,
      enviar,
      seguirGuiado,
      textoARestaurar,
      novaConversa,
      carregarConversa,
      retomada,
      consultarOperacao,
    ],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAssistente(): EstadoDoAssistente {
  const ctx = useContext(Ctx);
  if (ctx === null) throw new Error('useAssistente exige AssistenteProvider acima na árvore.');
  return ctx;
}
